import {
	Alert,
	Button,
	Code,
	CopyButton,
	Group,
	Modal,
	TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";

interface ApiKeyModalProps {
	opened: boolean;
	loading: boolean;
	// Set once the key has been created; the modal then shows it instead of the form
	createdKey: string | null;
	onClose: () => void;
	onSubmit: (name: string) => void;
}

function ApiKeyModal({
	opened,
	loading,
	createdKey,
	onClose,
	onSubmit,
}: ApiKeyModalProps) {
	const form = useForm({
		initialValues: { name: "" },
		validate: {
			name: (value) => (value.trim() ? null : "Name is required"),
		},
	});

	useEffect(() => {
		if (opened) form.reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [opened]);

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={createdKey ? "Copy the new API key" : "Create API Key"}
			closeOnClickOutside={!createdKey}
			centered>
			{createdKey ? (
				<>
					<Alert
						color="yellow"
						mb="md">
						This is the only time the key is shown. Copy it into the calling
						system now; if it is lost, deactivate it and create another.
					</Alert>
					<Code
						block
						mb="md">
						{createdKey}
					</Code>
					<Group justify="flex-end">
						<CopyButton value={createdKey}>
							{({ copied, copy }) => (
								<Button
									color={copied ? "green" : "violet"}
									onClick={copy}>
									{copied ? "Copied" : "Copy key"}
								</Button>
							)}
						</CopyButton>
						<Button
							variant="default"
							onClick={onClose}>
							Done
						</Button>
					</Group>
				</>
			) : (
				<form onSubmit={form.onSubmit((values) => onSubmit(values.name.trim()))}>
					<TextInput
						label="Name"
						description="Who or what will call the API with this key"
						placeholder="e.g. spaceOS staging"
						maxLength={100}
						data-autofocus
						{...form.getInputProps("name")}
					/>
					<Group
						justify="flex-end"
						mt="md">
						<Button
							variant="default"
							onClick={onClose}
							disabled={loading}>
							Cancel
						</Button>
						<Button
							type="submit"
							loading={loading}>
							Create
						</Button>
					</Group>
				</form>
			)}
		</Modal>
	);
}

export default ApiKeyModal;
